import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'

import { useAuthStore } from '../context'
import { defApi } from '../core-api'
import { SelectLangComponent } from '../shared'
import { Button, Input } from '../shared-ui'

import logo from './assets/LogoLight.svg'
import styles from './Auth.module.css'

type ErrorsType = {
  login?: string
  password?: string
}

const Auth = () => {
  const { t, i18n } = useTranslation()
  const { setToken, setClientId } = useAuthStore((store) => store)

  const [login, setLogin] = useState<string>()
  const [password, setPassword] = useState<string>()
  const [errors, setErrors] = useState<ErrorsType>({})
  const [requestError, setRequestError] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)

  const validate = () => {
    const newErrors: ErrorsType = {}
    if (!login?.trim()) newErrors.login = t('auth.login.login-required')
    if (!password?.trim()) newErrors.password = t('auth.login.password-required')

    return newErrors
  }

  const serviceUnavailable = t('auth.login.service-unavailable')
  const userNotFound = t('auth.login.user-not-found')

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    const validationErrors = validate()
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length === 0) {
      setIsLoading(true)
      setRequestError(undefined)

      try {
        const response = await defApi.cpaLoginPost({
          acceptLanguage: i18n.resolvedLanguage!,
          user: {
            email: login!,
            password: password!,
            devId: 'XXX'
          }
        })

        if (response.data.client) {
          const { client } = response.data

          if (client.refreshToken) {
            sessionStorage.setItem('refreshToken', client.refreshToken)
          }

          if (client.id) {
            setClientId(client.id)
          }

          if (client.accessToken) {
            setToken(client.accessToken)
          }
        }

        setRequestError(userNotFound)
        setIsLoading(false)
      } catch (error: any) {
        console.log('Auth error: ', error)

        setRequestError(error?.response?.data?.error?.message ? error.response.data.error.message : serviceUnavailable)
        setIsLoading(false)
      }
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.logoMask}>
        <img src={logo} alt='Logo' />
      </div>

      <div className={styles.content}>
        <SelectLangComponent className={styles.selectLang} />
        <form onSubmit={handleSubmit}>
          <Typography variant='h4' fontSize={34} fontWeight={600} marginBottom='32px'>
            {t('auth.login.sign-in')}
          </Typography>

          <div className={styles.formInputs}>
            <Input
              required
              className={styles.input}
              label={t('auth.login.login')}
              value={login}
              type='text'
              onChange={(value) => setLogin(value)}
              error={!!errors.login}
              // helperText={errors.login || ' '}
              fullWidth
            />
            <Input
              required
              className={styles.input}
              label={t('auth.login.password')}
              type='password'
              value={password}
              showPasswordIcon
              onChange={(value) => setPassword(value)}
              error={!!errors.password || !!errors.login}
              helperText={errors.login || errors.password ? [errors.login, errors.password].join('. ') : ' '}
              fullWidth
            />
            {requestError && (
              <Typography variant='body2' className={styles.errorText}>
                {requestError}
              </Typography>
            )}
          </div>
          <Button className={styles.btn} size='large' variant='contained' fullWidth type='submit' loading={isLoading}>
            {t('auth.login.enter')}
          </Button>
          <div className={styles.footerInfo}>
            <span>{t('auth.login.dont-have-account')}</span>
            <div>{t('auth.login.ask-about-account')}</div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Auth
